import type { CreditsTier } from '../types';
import { DeployEnv } from '@patsnap-utils/biz';
import { REGION } from '@eureka/utils/environment';
import { STRIPE_PRICE_IDS } from './price-ids';

export type CreditsPriceRegion = 'standard' | 'discount';
export type CreditsBillingCycle = 'monthly' | 'yearly';
export type StripeDeployEnv = 'stage' | 'cn' | 'us' | 'eu';

export interface CreditsTierPriceOption {
    tier: CreditsTier;
    credits: number;
    priceMonthly: number;
    priceYearlyMonthly: number;
}

interface IpInfoLiteResponse {
    country_code?: string;
    data?: {
        country_code?: string;
    };
}

let cachedCreditsPriceRegion: CreditsPriceRegion | null = null;
let pendingCreditsPriceRegionPromise: Promise<CreditsPriceRegion> | null = null;

const STANDARD_PRICE_COUNTRY_CODES = new Set([
    'GB', 'IE', 'DE', 'FR', 'NL', 'BE', 'LU', 'CH', 'AT', 'LI', 'MC',
    'NO', 'SE', 'DK', 'FI', 'IS', 'EE', 'LV', 'LT', 'IT', 'ES', 'PT',
    'GR', 'MT', 'CY', 'SI', 'HR', 'SM', 'VA', 'AD', 'PL', 'CZ', 'SK',
    'HU', 'RO', 'BG', 'RU', 'UA', 'BY', 'MD', 'RS', 'BA', 'MK', 'ME',
    'AL', 'XK', 'JP', 'SG', 'AU', 'NZ', 'KR', 'US', 'CA'
]);

export const DEFAULT_CREDITS_PRICE_REGION: CreditsPriceRegion = 'standard';

export const CREDITS_TIER_PRICE_OPTIONS: Record<CreditsPriceRegion, CreditsTierPriceOption[]> = {
    standard: [
        { tier: 'pro_100', credits: 10000, priceMonthly: 100, priceYearlyMonthly: 80 },
        { tier: 'pro_200', credits: 20000, priceMonthly: 200, priceYearlyMonthly: 160 },
        { tier: 'pro_400', credits: 40000, priceMonthly: 400, priceYearlyMonthly: 320 },
        { tier: 'pro_600', credits: 60000, priceMonthly: 600, priceYearlyMonthly: 480 }
    ],
    discount: [
        { tier: 'pro_100', credits: 10000, priceMonthly: 25, priceYearlyMonthly: 25 },
        { tier: 'pro_200', credits: 20000, priceMonthly: 50, priceYearlyMonthly: 50 },
        { tier: 'pro_400', credits: 40000, priceMonthly: 100, priceYearlyMonthly: 100 },
        { tier: 'pro_600', credits: 60000, priceMonthly: 150, priceYearlyMonthly: 150 }
    ],
};

export function resolveCreditsPriceRegion(countryCode?: string | null): CreditsPriceRegion {
    const normalizedCountryCode = countryCode?.trim().toUpperCase();
    if (!normalizedCountryCode) return DEFAULT_CREDITS_PRICE_REGION;
    return STANDARD_PRICE_COUNTRY_CODES.has(normalizedCountryCode) ? 'standard' : 'discount';
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => window.setTimeout(resolve, ms));
}

async function requestCurrentCreditsPriceRegion(timeoutMs: number): Promise<CreditsPriceRegion | null> {
    const abortController = new AbortController();
    const timeoutId = window.setTimeout(() => abortController.abort(), timeoutMs);

    try {
        const response = await fetch(`${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka/ipinfo/lite`, {
            credentials: 'include',
            signal: abortController.signal,
        });
        if (!response.ok) {
            throw new Error(`ipinfo lite request failed: ${response.status}`);
        }

        const result = await response.json() as IpInfoLiteResponse;
        const countryCode = result.country_code || result.data?.country_code;
        return countryCode ? resolveCreditsPriceRegion(countryCode) : null;
    } finally {
        window.clearTimeout(timeoutId);
    }
}

export async function fetchCurrentCreditsPriceRegion(timeoutMs = 5000): Promise<CreditsPriceRegion> {
    if (cachedCreditsPriceRegion) return cachedCreditsPriceRegion;
    if (pendingCreditsPriceRegionPromise) return pendingCreditsPriceRegionPromise;

    pendingCreditsPriceRegionPromise = (async() => {
        if (typeof window === 'undefined' || typeof fetch === 'undefined') {
            return DEFAULT_CREDITS_PRICE_REGION;
        }

        let lastError: unknown = null;
        try {
            for (let attempt = 0; attempt < 2; attempt += 1) {
                try {
                    const priceRegion = await requestCurrentCreditsPriceRegion(timeoutMs);
                    if (priceRegion) {
                        cachedCreditsPriceRegion = priceRegion;
                        return cachedCreditsPriceRegion;
                    }
                } catch (error) {
                    lastError = error;
                    if (attempt === 0) await sleep(150);
                }
            }
        } finally {
            pendingCreditsPriceRegionPromise = null;
        }

        if (lastError) {
            console.warn('Failed to resolve credits price region, using standard price region:', lastError);
        }
        return DEFAULT_CREDITS_PRICE_REGION;
    })();

    return pendingCreditsPriceRegionPromise;
}

export function resolveStripeDeployEnv(): StripeDeployEnv {
    if (REGION === DeployEnv.US) return 'us';
    if (REGION === DeployEnv.EU) return 'eu';
    if (REGION === DeployEnv.STAGE) return 'stage';
    return 'cn';
}

export function getCreditsTierPriceOption(
    tier: CreditsTier,
    priceRegion: CreditsPriceRegion = DEFAULT_CREDITS_PRICE_REGION
): CreditsTierPriceOption | null {
    if (tier === 'free') return null;
    return CREDITS_TIER_PRICE_OPTIONS[priceRegion].find(option => option.tier === tier) ?? null;
}

export function getCreditsStripePriceId(
    tier: CreditsTier,
    billingCycle: CreditsBillingCycle,
    priceRegion: CreditsPriceRegion = DEFAULT_CREDITS_PRICE_REGION
): string {
    if (tier === 'free') return '';

    const groupKey = priceRegion === 'discount'
        ? (billingCycle === 'monthly' ? 'creditsDiscount' : 'creditsDiscountYearly')
        : (billingCycle === 'monthly' ? 'credits' : 'creditsYearly');
    const group = STRIPE_PRICE_IDS[groupKey];
    const priceConfig = group[tier as keyof typeof group];
    if (!priceConfig) return '';
    const deployEnv = resolveStripeDeployEnv();
    if (priceRegion === 'discount') {
        return priceConfig[deployEnv];
    }
    return priceConfig[deployEnv] || priceConfig.stage;
}

export function resolveCreditsTierFromPriceId(priceId?: string | null): CreditsTier {
    if (!priceId) return 'free';

    for (const billingCycle of ['monthly', 'yearly'] as const) {
        for (const priceRegion of ['standard', 'discount'] as const) {
            for (const option of CREDITS_TIER_PRICE_OPTIONS[priceRegion]) {
                if (getCreditsStripePriceId(option.tier, billingCycle, priceRegion) === priceId) {
                    return option.tier;
                }
            }
        }
    }

    return 'free';
}
